// Marker Detection Function using OpenCV.js
// Detects square markers in images and returns the best marker coordinates

import i18n from '../config/i18n';
import { captureException } from '../utils/captureException';

declare global {
  // OpenCV.js global
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  var cv: any;
}

export type Point = { x: number; y: number };

export type MarkerDetectionResult = {
  corners: Point[] | null;
  area?: number;
  blackPercent?: number;
  whitePercent?: number;
  height: number;
  width: number;
};

/**
 * Converts an image source to a canvas element
 * Returns both the canvas and the object URL (if created) for cleanup
 */
function imageToCanvas(
  imageSource: File | HTMLImageElement | string
): Promise<{ canvas: HTMLCanvasElement; objectUrl?: string }> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error(i18n.t("errors.markerDetector.canvasNotSupported")));
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    let objectUrl: string | undefined;

    img.onload = () => {
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve({ canvas, objectUrl });
    };

    img.onerror = () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      reject(new Error(i18n.t("errors.markerDetector.failedToLoadImage")));
    };

    // Handle different input types
    if (imageSource instanceof File) {
      objectUrl = URL.createObjectURL(imageSource);
      img.src = objectUrl;
    } else if (imageSource instanceof HTMLImageElement) {
      img.src = imageSource.src;
    } else if (typeof imageSource === "string") {
      img.src = imageSource;
    } else {
      reject(new Error(i18n.t("errors.markerDetector.invalidImageSource")));
    }
  });
}

/**
 * Order corners as Top-Left, Top-Right, Bottom-Right, Bottom-Left
 */
function orderCorners(pts: Point[]): Point[] {
  const byY = [...pts].sort((a, b) => a.y - b.y);
  const top = byY.slice(0, 2).sort((a, b) => a.x - b.x);
  const bot = byY.slice(2, 4).sort((a, b) => a.x - b.x);
  return [top[0], top[1], bot[1], bot[0]];
}

/**
 * Calculate black/white pixel percentages in a region
 */
function calculatePixelPercentages(
  bwMat: any,
  contour: any
): { blackPercent: number; whitePercent: number } {
  const rect = window.cv.boundingRect(contour);
  const x = Math.max(0, Math.floor(rect.x));
  const y = Math.max(0, Math.floor(rect.y));
  const w = Math.min(rect.width, bwMat.cols - x);
  const h = Math.min(rect.height, bwMat.rows - y);

  let blackPixels = 0;
  let whitePixels = 0;
  const totalPixels = w * h;

  const data = bwMat.data;
  const cols = bwMat.cols;

  for (let row = y; row < y + h; row++) {
    for (let col = x; col < x + w; col++) {
      const idx = row * cols + col;
      const pixelValue = data[idx];
      if (pixelValue === 0) {
        blackPixels++;
      } else if (pixelValue === 255) {
        whitePixels++;
      }
    }
  }

  const blackPercent = totalPixels > 0 ? (blackPixels / totalPixels) * 100 : 0;
  const whitePercent = totalPixels > 0 ? (whitePixels / totalPixels) * 100 : 0;

  return { blackPercent, whitePercent };
}

/**
 * Calculate mean brightness of a grayscale image
 */
function calculateMeanBrightness(grayMat: any): number {
  const data = grayMat.data;
  let sum = 0;
  const totalPixels = data.length;

  for (let i = 0; i < totalPixels; i++) {
    sum += data[i];
  }

  return sum / totalPixels;
}

/**
 * Calculate maximum cosine between corners to check if angles are close to 90°
 */
function calculateMaxCosine(pts: Point[]): number {
  let maxCos = 0;

  for (let j = 0; j < 4; j++) {
    const p1 = pts[j];
    const p2 = pts[(j + 1) % 4];
    const p3 = pts[(j + 2) % 4];

    const v1x = p1.x - p2.x;
    const v1y = p1.y - p2.y;
    const v2x = p3.x - p2.x;
    const v2y = p3.y - p2.y;

    const m1 = Math.sqrt(v1x * v1x + v1y * v1y);
    const m2 = Math.sqrt(v2x * v2x + v2y * v2y);

    if (m1 * m2 > 0) {
      const cosine = Math.abs((v1x * v2x + v1y * v2y) / (m1 * m2));
      maxCos = Math.max(maxCos, cosine);
    }
  }

  return maxCos;
}

/**
 * Detects the best marker in an image and returns its coordinates
 * @param imageSource - Image file, HTMLImageElement, or image URL string
 * @returns Promise with marker detection result containing corners and metadata
 */
export async function detectMarker(
  imageSource: File | HTMLImageElement | string
): Promise<MarkerDetectionResult> {
  // Check if OpenCV is available
  if (!window.cv || !window.cv.Mat) {
    throw new Error(i18n.t("errors.markerDetector.opencvNotLoaded"));
  }

  // Convert image to canvas
  const { canvas: sourceCanvas, objectUrl } = await imageToCanvas(imageSource);

  // OpenCV: read from canvas
  const src = window.cv.imread(sourceCanvas);
  const gray = new window.cv.Mat();
  // const erode = new window.cv.Mat();
  // const dilate = new window.cv.Mat();
  const blurred = new window.cv.Mat();
  const bw = new window.cv.Mat();
  const bwClean = new window.cv.Mat();
  const contours = new window.cv.MatVector();
  const hierarchy = new window.cv.Mat();

  try {
      // 1. Convert to grayscale
    window.cv.cvtColor(src, gray, window.cv.COLOR_BGR2GRAY);

    // 2. Apply Gaussian blur
    const ksize = new window.cv.Size(5, 5);
    window.cv.GaussianBlur(gray, blurred, ksize, 0, 0, window.cv.BORDER_DEFAULT);

    // 3. Calculate mean brightness to determine threshold mode
    const meanBrightness = calculateMeanBrightness(blurred);
    let usedInv = false;
    let modeName = "";

    if (meanBrightness > 127) {
      // Light background, dark marker
      window.cv.threshold(blurred, bw, 0, 255, window.cv.THRESH_BINARY_INV | window.cv.THRESH_OTSU);
      usedInv = true;
      modeName = "INV (Dark Marker on Light Background)";
    } else {
      // Dark background, light marker
      window.cv.threshold(blurred, bw, 0, 255, window.cv.THRESH_BINARY | window.cv.THRESH_OTSU);
      usedInv = false;
      modeName = "NORM (Light Marker on Dark Background)";
    }

    console.log(`Mode selected: ${modeName} (Mean brightness: ${meanBrightness.toFixed(1)})`);

    // Invert if needed for consistent processing
    if (usedInv) {
      window.cv.bitwise_not(bw, bw);
    }

    // 4. Morphological operations
    const kernel = window.cv.getStructuringElement(window.cv.MORPH_RECT, new window.cv.Size(3, 3));
    window.cv.morphologyEx(bw, bwClean, window.cv.MORPH_OPEN, kernel, new window.cv.Point(-1, -1), 1);
    window.cv.morphologyEx(bwClean, bwClean, window.cv.MORPH_CLOSE, kernel, new window.cv.Point(-1, -1), 1);
    kernel.delete();

    // 5. Find contours
    window.cv.findContours(bwClean, contours, hierarchy, window.cv.RETR_TREE, window.cv.CHAIN_APPROX_SIMPLE);

    let bestCandidate: { cnt: any; approx: any; area: number } | null = null;
    let bestScore = Infinity; // Now we look for minimum max cosine

    // 6. Evaluate all contours
    for (let i = 0; i < contours.size(); i++) {
      const cnt = contours.get(i);
      const area = window.cv.contourArea(cnt);

      if (area < 1000) {
        cnt.delete();
        continue;
      }

      const peri = window.cv.arcLength(cnt, true);
      const approx = new window.cv.Mat();
      window.cv.approxPolyDP(cnt, approx, 0.04 * peri, true);

      if (approx.rows === 4 && window.cv.isContourConvex(approx)) {
        // [1] Aspect Ratio Filter
        const rect = window.cv.minAreaRect(approx);
        const w = rect.size.width;
        const h = rect.size.height;
        const ar = Math.max(w, h) / (Math.min(w, h) + 1e-5);

        if (ar < 1.2) {
          // [2] Cosine Filter
          const pts: Point[] = [];
          for (let r = 0; r < 4; r++) {
            const x = approx.intPtr(r, 0)[0];
            const y = approx.intPtr(r, 0)[1];
            pts.push({ x, y });
          }

          const maxCos = calculateMaxCosine(pts);

          if (maxCos < 0.09) {
            // [3] Content Filter (Pixel Percentages)
            let validContent = true;
            try {
              const { blackPercent, whitePercent } = calculatePixelPercentages(bwClean, cnt);

              if (blackPercent >= 73.0 && blackPercent <= 83.5 && whitePercent >= 17.0 && whitePercent <= 30.0) {
                validContent = true;
              } else {
                validContent = false;
              }
            } catch (error) {
              captureException(error, { tags: { feature: 'markerDetector' }, extra: { action: 'calculatePixelPercentages' } });
              console.log(`Error calculating percentages: ${error}`);
              validContent = true; // Accept by geometry if calculation fails
            }

            if (validContent) {
              // Select candidate with smallest max cosine
              const score = maxCos;
              if (score < bestScore) {
                if (bestCandidate) {
                  bestCandidate.cnt.delete();
                  bestCandidate.approx.delete();
                }
                bestScore = score;
                bestCandidate = { cnt: cnt.clone(), approx: approx.clone(), area };
              }
            }
          }
        }
      }

      cnt.delete();
      approx.delete();
    }

    let corners: Point[] | null = null;
    let finalArea: number | undefined;

    if (bestCandidate) {
      console.log(`MARKER FOUND! Area=${bestCandidate.area.toFixed(0)}, MaxCos=${bestScore.toFixed(4)}`);

      const pts: Point[] = [];
      for (let r = 0; r < 4; r++) {
        const x = bestCandidate.approx.intPtr(r, 0)[0];
        const y = bestCandidate.approx.intPtr(r, 0)[1];
        pts.push({ x, y });
      }

      // Order corners
      corners = orderCorners(pts);

      // Sub-pixel refinement
      const cornersArray = new window.cv.Mat(4, 1, window.cv.CV_32FC2);
      for (let i = 0; i < 4; i++) {
        cornersArray.data32F[i * 2] = corners[i].x;
        cornersArray.data32F[i * 2 + 1] = corners[i].y;
      }

      // const criteria = new window.cv.TermCriteria(
      //   window.cv.TERM_CRITERIA_EPS | window.cv.TERM_CRITERIA_MAX_ITER,
      //   100,
      //   0.001
      // );
      // const winSize = new window.cv.Size(5, 5);
      // const zeroZone = new window.cv.Size(-1, -1);

      // window.cv.cornerSubPix(gray, cornersArray, winSize, zeroZone, criteria);

      // Extract refined corners
      const refinedCorners: Point[] = [];
      for (let i = 0; i < 4; i++) {
        refinedCorners.push({
          x: cornersArray.data32F[i * 2],
          y: cornersArray.data32F[i * 2 + 1]
        });
      }
      corners = refinedCorners;

      cornersArray.delete();
      finalArea = bestCandidate.area;

      bestCandidate.cnt.delete();
      bestCandidate.approx.delete();
    } else {
      console.log("*** NO VALID MARKER FOUND ***");
    }

    console.log("Best candidate corners", corners);

    const height = src.rows;
    const width = src.cols;

    // Cleanup
    contours.delete();
    hierarchy.delete();
    src.delete();
    gray.delete();
    blurred.delete();
    bw.delete();
    bwClean.delete();

    // Clean up object URL if created from File
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
    if (!corners) {
      throw new Error(i18n.t("errors.markerDetector.noMarkerDetected"));
    } else if (corners.length !== 4) {
      throw new Error(i18n.t("errors.markerDetector.invalidCorners"));
    }

    return {
      corners,
      area: finalArea,
      height,
      width,
    };
  } catch (error) {
    // Cleanup on error
    try {
        contours.delete();
        hierarchy.delete();
        src.delete();
        gray.delete();
        blurred.delete();
        bw.delete();
        bwClean.delete();

        // Clean up object URL if created from File
        if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        }
    } catch (cleanupError) {
        captureException(cleanupError, { tags: { feature: 'markerDetector' }, extra: { action: 'cleanupOnError' } });
        console.log("Error deleting images", cleanupError);
    }

    throw error;
  }
}

